// Weather API wrapper.

import type {
  CurrentConditions,
  SprayWindow,
  WeatherAlert,
  WeatherForecastResponse,
} from '@greeniq/shared';
import { api } from './client.js';

export interface HistoricalDayRow {
  id?: number;
  observationDate: string;
  tempMaxF: number | null;
  tempMinF: number | null;
  precipitationIn: number | null;
  precipitationProbabilityPct: number | null;
  relativeHumidityPct: number | null;
  windSpeedMph: number | null;
  soilTemp6cmF: number | null;
  soilTemp18cmF: number | null;
  et0In: number | null;
  gddBase50: number | null;
  gddBase60: number | null;
  isForecast?: boolean;
}

export interface HistoryResponse {
  days: HistoricalDayRow[];
  source: 'db' | 'open-meteo';
}

export function getWeatherForecast(courseSlug: string): Promise<WeatherForecastResponse> {
  return api.get<WeatherForecastResponse>(`/courses/${courseSlug}/weather/forecast`);
}

export interface CurrentWeatherResponse {
  current: CurrentConditions;
  timezone: string;
  source: 'open-meteo';
}

export function getWeatherCurrent(courseSlug: string): Promise<CurrentWeatherResponse> {
  return api.get<CurrentWeatherResponse>(`/courses/${courseSlug}/weather/current`);
}

export function getWeatherHistory(
  courseSlug: string,
  fromDate: string,
  toDate: string,
): Promise<HistoryResponse> {
  const params = new URLSearchParams({ from: fromDate, to: toDate });
  return api.get<HistoryResponse>(
    `/courses/${courseSlug}/weather/history?${params.toString()}`,
  );
}

export interface SprayWindowsResponse {
  windows: SprayWindow[];
  timezone: string;
}

export function getSprayWindows(
  courseSlug: string,
  limit = 5,
): Promise<SprayWindowsResponse> {
  return api.get<SprayWindowsResponse>(
    `/courses/${courseSlug}/weather/spray-windows?limit=${limit}`,
  );
}

export interface WeatherAlertsResponse {
  alerts: WeatherAlert[];
}

export function getWeatherAlerts(
  courseSlug: string,
  status: 'unacknowledged' | 'acknowledged' | 'dismissed' | 'all' = 'unacknowledged',
): Promise<WeatherAlertsResponse> {
  return api.get<WeatherAlertsResponse>(
    `/courses/${courseSlug}/weather/alerts?status=${status}`,
  );
}

export function acknowledgeWeatherAlert(
  courseSlug: string,
  alertId: number,
): Promise<{ alert: WeatherAlert }> {
  return api.post<{ alert: WeatherAlert }>(
    `/courses/${courseSlug}/weather/alerts/${alertId}/acknowledge`,
  );
}
